import { useState, useMemo, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Spinner, Text } from '@fluentui/react';
import AffinityContentTable from './AffinityContentTable';
import { useBoolean } from "@fluentui/react-hooks";
import ErrorComponent from '../../../../Util/ErrorComponent';
import MessageBarComponent from '../../../../Util/MessageBarComponent';
import FluentSelect from '../../../../Forms/FluentSelect';
import ChoiceGroup from '../../../../Forms/ChoiceGroup';
import DeleteContentModal from '../../Series/Helpers/DeleteContentModal';
import { tagOrPrimaryContentOptions } from '../Helpers/CopyAndFunctions';

const MAX_SELECTABLE = 100;

const AffinityContent = ({ tagField, fieldName, isSecondary = false, defaultToTagIfEmpty = false }) => {
  const { setFieldValue, getFieldProps, values } = useFormikContext();
  const fieldValue = getFieldProps(fieldName).value || [];
  const tagFieldValue = getFieldProps(tagField).value || '';

  // Determine which flag and options to use
  const tagOrContentKey = isSecondary ? 'tagOrSecondaryContent' : 'tagOrPrimaryContent';
  const tagOrContentOptions = tagOrPrimaryContentOptions; 
  const [tagOrContentValue, setTagOrContentValue] = useState(values[tagOrContentKey]);

  useEffect(() => {
    setTagOrContentValue(values[tagOrContentKey]);
  }, [values[tagOrContentKey]]);

  // If defaultToTagIfEmpty is true and array is empty, default to 'tag'
  useEffect(() => {
    if (defaultToTagIfEmpty && (!fieldValue || fieldValue.length === 0)) {
      setTagOrContentValue('tag');
      setFieldValue(tagOrContentKey, 'tag');
    } else if (!tagOrContentValue) {
      if (tagFieldValue) {
        setTagOrContentValue('tag');
        setFieldValue(tagOrContentKey, 'tag');
      } else if (fieldValue && fieldValue.length > 0) {
        setTagOrContentValue('content');
        setFieldValue(tagOrContentKey, 'content');
      } else {
        setTagOrContentValue(null);
        setFieldValue(tagOrContentKey, null);
      }
    }
  }, [defaultToTagIfEmpty, fieldValue, tagFieldValue]);

  // Initialize resource options based on tagOrContentValue
  const initialResources = tagOrContentValue === 'tag' ?  values.dataOptions.topics : tagOrContentValue === 'content' ? values.dataOptions.content : [];
  const firstResource = tagOrContentValue === 'tag' ?  tagFieldValue : tagOrContentValue === 'content' ? fieldValue : null;
  const [resourceOptions, setResourceOptions] = useState(initialResources || []);
  const [selectedResources, setSelectedResources] = useState(firstResource || []);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showAddResource, setShowAddResource] = useState(false);
  const [showDeleteModal, { toggle: toggleDeleteModal }] = useBoolean(false);

  const affinityContent = firstResource || [];
  const noEligibleData = !resourceOptions || resourceOptions.length === 0;
  const loading = useMemo(() => noEligibleData, [noEligibleData]);

  useEffect(() => {
    // Reset resource options when tagOrContentValue changes
    if (tagOrContentValue === 'tag') {
      setResourceOptions(values.dataOptions.topics || []);
      setSelectedResources(tagFieldValue ? tagFieldValue : []);
    } else if (tagOrContentValue === 'content') {
      setResourceOptions(values.dataOptions.content || []);
      setSelectedResources(fieldValue.length > 0 ? fieldValue : []);
    } else {
      setResourceOptions([]);
    }
  }, [tagOrContentValue, tagFieldValue, fieldValue, values.dataOptions]);

  const handleRemoveContent = (item) => {
    const updated = affinityContent.filter(c => c.id !== item.id);
    setResourceOptions(updated);
    setFieldValue(fieldName, updated);
    setShowSuccess(true);
  };

  const mapNewContent = (id) => {
    if (!id) return;
    const item = resourceOptions.find(x => x.contentId === id.value);
    if (selectedResources.some(c => c.contentId === item.contentId)) return; // Prevent duplicates
    item.order = selectedResources.length + 1; // Assign order based on current length
    const newlist = [...selectedResources, item];
    if (newlist.length > MAX_SELECTABLE) {
        setShowError(true);
        setErrorMsg(`You can only select up to ${MAX_SELECTABLE} resources.`);
        return;
    }
    setFieldValue(fieldName, newlist);
    setFieldValue(tagOrContentKey, 'content');
    setTagOrContentValue('content');
    setSelectedResources(newlist);
    setShowAddResource(false);
    setShowSuccess(true);
  };

  if (loading) return <Spinner className="absolute-center" label="Loading Affinity Content..." />;

  return (
    <>
      <MessageBarComponent
        message="Affinity content updated. Remember to save."
        showMessage={showSuccess}
        setShowMessage={setShowSuccess}
      />
      <ErrorComponent
        errorMessage={errorMsg}
        showError={showError}
        setShowError={setShowError}
      />
      <ChoiceGroup label={isSecondary ? "Secondary Content" : "Primary Content"} options={tagOrContentOptions} name={tagOrContentKey}
        selectedKey={tagOrContentValue}
        onChange={(_, option) => {
          setTagOrContentValue(option.key);
          setFieldValue(tagOrContentKey, option.key);
        }}
      />
      <Text variant="small">
        {tagOrContentValue === 'tag' ? "Content will be set based on topic" : `Select up to ${MAX_SELECTABLE} pieces of content to add to your affinity group.`}
      </Text>
      {tagOrContentValue === 'tag' && (
        <div className="max-800">
          <FluentSelect
            options={resourceOptions && resourceOptions.map(t => ({ value: t.conTagID, label: `${t.conTagID} - ${t.tagName} - ${t.tagSlug}` }))}
            label='Eligible Tags'
            name={tagField}
            component={FluentSelect}
          />
          <label className="preview-upload-help">Search for tags by Title or ID. Then select to add to your affinity group.</label>
        </div>
      )}
      {tagOrContentValue === 'content' && (
        <>
          {showAddResource && (
            <div className="max-800">
              <FluentSelect
                options={resourceOptions && resourceOptions.map(c => ({ value: c.contentId, label: `${c.contentId} - ${c.title} - ${c.type}` }))}
                onChange={mapNewContent}
                label='Eligible Content'
                component={FluentSelect}
              />
              <label className="preview-upload-help">Search for content by Title or ID. Then select to add to your affinity group.</label>
            </div>
          )}
          <AffinityContentTable
            content={affinityContent.sort((a, b) => (b.order - a.order))}
            selectedContent={selectedResources}
            setSelectedContent={setSelectedResources}
            createOnClick={() => setShowAddResource(true)}
            deleteOnClick={() => toggleDeleteModal()}
            maxSelectable={MAX_SELECTABLE}
            showAddContent={showAddResource}
            loading={loading}
          />
        </>
      )}
      <DeleteContentModal
        showModal={showDeleteModal}
        toggleShowModal={toggleDeleteModal}
        handleDelete={() => handleRemoveContent()}
        setErrorMsg={setErrorMsg}
        setShowError={setShowError}
        setShowSuccess={setShowSuccess}
        subText='Are you sure you want to delete this content from the group?'
        />
    </>
  );
};

export default AffinityContent;
